/** Kleine Animations-Helfer. Robust gegen das Full-Re-Render-Pattern:
 *  laufende rAF-Schleifen brechen ab, sobald ihr Element aus dem DOM fliegt. */

export function prefersReducedMotion(): boolean {
  return typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** Zählt eine Zahl im Element von `from` auf `to` hoch (ease-out). */
export function animateCount(el: HTMLElement, from: number, to: number, ms = 650): void {
  if (from === to || prefersReducedMotion()) { el.textContent = String(to); return; }
  const start = performance.now();
  const step = (now: number): void => {
    if (!el.isConnected) return; // Element wurde wegrendert → Schleife beenden
    const t = Math.min(1, (now - start) / ms);
    const eased = 1 - Math.pow(1 - t, 3);
    el.textContent = String(Math.round(from + (to - from) * eased));
    if (t < 1) requestAnimationFrame(step);
    else el.textContent = String(to);
  };
  requestAnimationFrame(step);
}

const CONFETTI_COLORS = ['#e0a850', '#c4923a', '#f5e8cf', '#b96a2a', '#c84d3c', '#7e6b4c'];

type Particle = {
  x: number; y: number; vx: number; vy: number;
  rot: number; vr: number; size: number; color: string; shape: 0 | 1;
};

/** Goldener Konfetti-Regen über den ganzen Viewport — für den Sieg. */
export function celebrate(durationMs = 2800): void {
  if (prefersReducedMotion()) return;
  const canvas = document.createElement('canvas');
  canvas.className = 'fx-confetti';
  const cssW = window.innerWidth;
  const cssH = window.innerHeight;
  const dpr = Math.min(2, window.devicePixelRatio || 1);
  canvas.width = cssW * dpr;
  canvas.height = cssH * dpr;
  document.body.appendChild(canvas);
  const ctx = canvas.getContext('2d');
  if (!ctx) { canvas.remove(); return; }
  ctx.scale(dpr, dpr);

  const count = Math.min(180, Math.floor(cssW / 6));
  const parts: Particle[] = [];
  for (let i = 0; i < count; i++) {
    parts.push({
      x: Math.random() * cssW,
      y: -20 - Math.random() * cssH * 0.5,
      vx: (Math.random() - 0.5) * 1.2,
      vy: 2 + Math.random() * 3.5,
      rot: Math.random() * Math.PI,
      vr: (Math.random() - 0.5) * 0.3,
      size: 5 + Math.random() * 7,
      color: CONFETTI_COLORS[(Math.random() * CONFETTI_COLORS.length) | 0],
      shape: Math.random() < 0.5 ? 0 : 1,
    });
  }

  const start = performance.now();
  const frame = (now: number): void => {
    const elapsed = now - start;
    const fade = elapsed > durationMs - 600 ? Math.max(0, (durationMs - elapsed) / 600) : 1;
    ctx.clearRect(0, 0, cssW, cssH);
    ctx.globalAlpha = fade;
    for (const p of parts) {
      p.x += p.vx;
      p.y += p.vy;
      p.vy += 0.04; // Schwerkraft
      p.rot += p.vr;
      ctx.save();
      ctx.translate(p.x, p.y);
      ctx.rotate(p.rot);
      ctx.fillStyle = p.color;
      if (p.shape === 0) ctx.fillRect(-p.size / 2, -p.size / 2, p.size, p.size * 0.6);
      else { ctx.beginPath(); ctx.arc(0, 0, p.size / 2, 0, Math.PI * 2); ctx.fill(); }
      ctx.restore();
    }
    if (elapsed < durationMs) requestAnimationFrame(frame);
    else canvas.remove();
  };
  requestAnimationFrame(frame);
}
